import { Photo } from '../types';

export interface PhotoGroup {
  monthYear: string;
  displayDate: string;
  photos: Photo[];
}

/**
 * Get the display date for a photo (EXIF date if available, otherwise created date)
 */
export function getPhotoDisplayDate(photo: Photo): Date {
  // Try to use EXIF date first
  if (photo.metadata.exifDate) {
    const exifDate = new Date(photo.metadata.exifDate);
    // Validate the EXIF date is reasonable (between 1990 and current year + 1)
    const currentYear = new Date().getFullYear();
    if (exifDate.getFullYear() >= 1990 && exifDate.getFullYear() <= currentYear + 1) {
      return exifDate;
    }
  }
  
  // Fall back to created date
  return new Date(photo.createdAt);
}

/**
 * Group photos by month and year
 */
export function groupPhotosByMonth(photos: Photo[]): PhotoGroup[] {
  const groups = new Map<string, Photo[]>();
  
  photos.forEach(photo => {
    const displayDate = getPhotoDisplayDate(photo);
    const monthYear = `${displayDate.getFullYear()}-${String(displayDate.getMonth() + 1).padStart(2, '0')}`;
    
    if (!groups.has(monthYear)) {
      groups.set(monthYear, []);
    }
    groups.get(monthYear)!.push(photo);
  });
  
  // Convert to array and sort by date (newest first)
  const sortedGroups = Array.from(groups.entries())
    .sort(([a], [b]) => b.localeCompare(a))
    .map(([monthYear, photos]) => {
      const [year, month] = monthYear.split('-');
      const date = new Date(parseInt(year), parseInt(month) - 1);
      
      return {
        monthYear,
        displayDate: date.toLocaleDateString('en-US', { 
          year: 'numeric', 
          month: 'long' 
        }),
        photos: photos.sort((a, b) => {
          const dateA = getPhotoDisplayDate(a);
          const dateB = getPhotoDisplayDate(b);
          return dateB.getTime() - dateA.getTime(); // Newest first within month
        })
      };
    });
  
  return sortedGroups;
}

/**
 * Format a date for display
 */
export function formatDisplayDate(date: Date): string {
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
}